import React from 'react';
import {
    Input,
    Icon,
    Dropdown,
    Tooltip
} from 'gd-react';
import './ModifierEditor.scss';

class ModifierEditor extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            modifier: props.modifier || {
                operations: [],
                description: ''
            },
            expandedOperations: new Set(), // Track which operations are expanded
            scrollToOperation: null // Track which operation to scroll to
        };
        
        this.operationsContainerRef = React.createRef();
        
        this.updateModifier = this.updateModifier.bind(this);
        this.addOperation = this.addOperation.bind(this);
        this.removeOperation = this.removeOperation.bind(this);
        this.updateOperation = this.updateOperation.bind(this);
        this.addParameter = this.addParameter.bind(this);
        this.removeParameter = this.removeParameter.bind(this);
        this.updateParameter = this.updateParameter.bind(this);
        this.moveOperationUp = this.moveOperationUp.bind(this);
        this.moveOperationDown = this.moveOperationDown.bind(this);
        this.toggleOperationExpanded = this.toggleOperationExpanded.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.modifier !== this.props.modifier) {
            this.setState({ modifier: this.props.modifier || {
                operations: [],
                description: ''
            }});
        }
        
        // Handle scrolling to new operations
        if (this.state.scrollToOperation !== null && this.state.scrollToOperation !== prevState.scrollToOperation) {
            setTimeout(() => {
                const operationElements = document.querySelectorAll('.operation-item');
                const editorContent = document.querySelector('.editor-content');
                
                if (operationElements[this.state.scrollToOperation] && editorContent) {
                    const targetElement = operationElements[this.state.scrollToOperation];
                    const containerRect = editorContent.getBoundingClientRect();
                    const targetRect = targetElement.getBoundingClientRect();
                    
                    const scrollTop = targetElement.offsetTop - editorContent.offsetTop - (containerRect.height / 2) + (targetRect.height / 2);
                    
                    editorContent.scrollTo({
                        top: scrollTop,
                        behavior: 'smooth'
                    });
                }
                this.setState({ scrollToOperation: null });
            }, 100);
        }
    }

    updateModifier(field, value) {
        const newModifier = {
            ...this.state.modifier,
            [field]: value
        };
        
        this.setState({ modifier: newModifier }, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.modifier);
            }
        });
    }

    addOperation() {
        const newOperations = [...(this.state.modifier.operations || [])];
        const newOperationIndex = newOperations.length;
        
        newOperations.push({
            order: newOperations.length + 1,
            operation: 'multiply',
            parameters: [],
            description: ''
        });
        
        const newModifier = {
            ...this.state.modifier,
            operations: newOperations
        };
        
        this.setState({ 
            modifier: newModifier,
            expandedOperations: new Set([...this.state.expandedOperations, newOperationIndex]),
            scrollToOperation: newOperationIndex
        }, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.modifier);
            }
        });
    }

    removeOperation(operationIndex) {
        const newOperations = [...(this.state.modifier.operations || [])];
        newOperations.splice(operationIndex, 1);
        
        // Reorder operations
        newOperations.forEach((op, index) => {
            op.order = index + 1;
        });
        
        const newModifier = {
            ...this.state.modifier,
            operations: newOperations
        };
        
        this.setState({ modifier: newModifier }, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.modifier);
            }
        });
    }

    updateOperation(operationIndex, field, value) {
        const newOperations = [...(this.state.modifier.operations || [])];
        newOperations[operationIndex] = {
            ...newOperations[operationIndex],
            [field]: value
        };
        
        const newModifier = {
            ...this.state.modifier,
            operations: newOperations
        };
        
        this.setState({ modifier: newModifier }, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.modifier);
            }
        });
    }

    addParameter(operationIndex) {
        const newOperations = [...(this.state.modifier.operations || [])];
        const operation = newOperations[operationIndex];
        const newParameters = [...(operation.parameters || [])];
        
        newParameters.push({
            name: `param_${Date.now()}`,
            value: '',
            description: ''
        });
        
        newOperations[operationIndex] = {
            ...operation,
            parameters: newParameters
        };
        
        const newModifier = {
            ...this.state.modifier,
            operations: newOperations
        };
        
        this.setState({ modifier: newModifier }, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.modifier);
            }
        });
    }

    removeParameter(operationIndex, parameterIndex) {
        const newOperations = [...(this.state.modifier.operations || [])];
        const operation = newOperations[operationIndex];
        const newParameters = [...(operation.parameters || [])];
        
        newParameters.splice(parameterIndex, 1);
        
        newOperations[operationIndex] = {
            ...operation,
            parameters: newParameters
        };
        
        const newModifier = {
            ...this.state.modifier,
            operations: newOperations
        };
        
        this.setState({ modifier: newModifier }, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.modifier);
            }
        });
    }

    updateParameter(operationIndex, parameterIndex, field, value) {
        const newOperations = [...(this.state.modifier.operations || [])];
        const operation = newOperations[operationIndex];
        const newParameters = [...(operation.parameters || [])];
        
        newParameters[parameterIndex] = {
            ...newParameters[parameterIndex],
            [field]: value
        };
        
        newOperations[operationIndex] = {
            ...operation,
            parameters: newParameters
        };
        
        const newModifier = {
            ...this.state.modifier,
            operations: newOperations
        };
        
        this.setState({ modifier: newModifier }, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.modifier);
            }
        });
    }

    moveOperationUp(operationIndex) {
        const newOperations = [...(this.state.modifier.operations || [])];
        if (operationIndex > 0) {
            [newOperations[operationIndex], newOperations[operationIndex - 1]] = [newOperations[operationIndex - 1], newOperations[operationIndex]];
            newOperations.forEach((op, index) => {
                op.order = index + 1;
            });
            this.setState({ modifier: { ...this.state.modifier, operations: newOperations } }, () => {
                if (this.props.onChange) {
                    this.props.onChange(this.state.modifier);
                }
            });
        }
    }

    moveOperationDown(operationIndex) {
        const newOperations = [...(this.state.modifier.operations || [])];
        if (operationIndex < newOperations.length - 1) {
            [newOperations[operationIndex], newOperations[operationIndex + 1]] = [newOperations[operationIndex + 1], newOperations[operationIndex]];
            newOperations.forEach((op, index) => {
                op.order = index + 1;
            });
            this.setState({ modifier: { ...this.state.modifier, operations: newOperations } }, () => {
                if (this.props.onChange) {
                    this.props.onChange(this.state.modifier);
                }
            });
        }
    }

    toggleOperationExpanded(operationIndex) {
        const newExpandedOperations = new Set(this.state.expandedOperations);
        if (newExpandedOperations.has(operationIndex)) {
            newExpandedOperations.delete(operationIndex);
        } else {
            newExpandedOperations.add(operationIndex);
        }
        this.setState({ expandedOperations: newExpandedOperations });
    }

    getOperationOptions() {
        return [
            { value: 'multiply', title: 'Multiply (×)' },
            { value: 'divide', title: 'Divide (÷)' },
            { value: 'add', title: 'Add (+)' },
            { value: 'subtract', title: 'Subtract (-)' },
            { value: 'power', title: 'Power (^)' },
            { value: 'sqrt', title: 'Square Root (√)' },
            { value: 'log', title: 'Logarithm (log)' },
            { value: 'exp', title: 'Exponential (e^x)' }
        ];
    }

    getOperationSummary(operation) {
        const operationType = operation.operation || 'multiply';
        const parameters = operation.parameters || [];
        
        if (parameters.length > 0) {
            // Calculate total of all parameter values
            const total = parameters.reduce((sum, param) => {
                const value = parseFloat(param.value) || 0;
                return sum + value;
            }, 0);
            
            if (total > 0) {
                return { operationType, total };
            }
        }
        
        return { operationType };
    }

    render() {
        const { modifier } = this.state;
        const operations = modifier.operations || [];
        const operationOptions = this.getOperationOptions();

        return (
            <div className="modifier-editor">
                <div className="editor-content">
                    <div className="form-section">
                        <div className="section-header">
                            <h4>Formula Operations</h4>
                            <Tooltip label="Add operation" position="top">
                                <Icon
                                    icon="FiPlus"
                                    size={16}
                                    color="gd-green"
                                    onIconClick={this.addOperation}
                                    className="add-operation-icon"
                                />
                            </Tooltip>
                        </div>

                        {operations.length === 0 ? (
                            <div className="no-operations">
                                <p>No operations configured. Add an operation to define your formula.</p>
                            </div>
                        ) : (
                            <div className="operations-list">
                                {operations.map((operation, operationIndex) => {
                                    const isExpanded = this.state.expandedOperations.has(operationIndex);
                                    
                                    return (
                                        <div key={operationIndex} className="operation-item">
                                            <div className="operation-header clickable" 
                                                 onClick={() => this.toggleOperationExpanded(operationIndex)}>
                                                <div className="operation-info">
                                                    <div className="operation-controls">
                                                        {operationIndex > 0 && (
                                                            <Tooltip label="Move up" position="top">
                                                                <Icon
                                                                    icon="FiArrowUp"
                                                                    size={14}
                                                                    color="gd-blue"
                                                                    onIconClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.moveOperationUp(operationIndex);
                                                                    }}
                                                                    className="move-up-icon"
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        {operationIndex < operations.length - 1 && (
                                                            <Tooltip label="Move down" position="top">
                                                                <Icon
                                                                    icon="FiArrowDown"
                                                                    size={14}
                                                                    color="gd-blue"
                                                                    onIconClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.moveOperationDown(operationIndex);
                                                                    }}
                                                                    className="move-down-icon"
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                    <span className="operation-number">#{operation.order}</span>
                                                    <div className="operation-summary">
                                                        <span className="operation-type">
                                                            {this.getOperationSummary(operation).operationType}
                                                            {this.getOperationSummary(operation).total && (
                                                                <>
                                                                    <span className="operation-by"> by </span>
                                                                    <span className="operation-value">{this.getOperationSummary(operation).total}</span>
                                                                </>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="operation-actions">
                                                    <Tooltip label={isExpanded ? "Collapse" : "Expand"} position="top">
                                                        <Icon
                                                            icon={isExpanded ? "FiChevronUp" : "FiChevronDown"}
                                                            size={14}
                                                            color="gd-blue"
                                                            onIconClick={(e) => {
                                                                e.stopPropagation();
                                                                this.toggleOperationExpanded(operationIndex);
                                                            }}
                                                            className="expand-toggle-icon"
                                                        />
                                                    </Tooltip>
                                                    <Tooltip label="Remove operation" position="top">
                                                        <Icon
                                                            icon="FiTrash2"
                                                            size={14}
                                                            color="gd-red"
                                                            onIconClick={(e) => {
                                                                e.stopPropagation();
                                                                this.removeOperation(operationIndex);
                                                            }}
                                                            className="remove-operation-icon"
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            
                                            {operation.description && !isExpanded && (
                                                <div className="operation-description-collapsed">
                                                    {operation.description}
                                                </div>
                                            )}
                                            
                                            {isExpanded && (
                                                <div className="operation-details">
                                                    <div className="operation-fields">
                                                        <Dropdown
                                                            label="Operation Type"
                                                            fixeditems={operationOptions}
                                                            value={operation.operation}
                                                            onChange={(value) => this.updateOperation(operationIndex, 'operation', value)}
                                                            placeholder="Select operation"
                                                            additionalclasses="sm"
                                                        />
                                                        <Input
                                                            label="Description"
                                                            value={operation.description}
                                                            onChange={(e) => this.updateOperation(operationIndex, 'description', e.target.value)}
                                                            placeholder="What does this operation do?"
                                                        />
                                                    </div>

                                                    <div className="parameters-section">
                                                        <div className="parameters-header">
                                                            <span className="parameters-label">Parameters</span>
                                                            <Tooltip label="Add parameter" position="top">
                                                                <Icon
                                                                    icon="FiPlus"
                                                                    size={14}
                                                                    color="gd-blue"
                                                                    onIconClick={() => this.addParameter(operationIndex)}
                                                                    className="add-parameter-icon"
                                                                />
                                                            </Tooltip>
                                                        </div>

                                                        {operation.parameters && operation.parameters.length > 0 ? (
                                                            <div className="parameters-list">
                                                                {operation.parameters.map((parameter, parameterIndex) => (
                                                                    <div key={parameterIndex} className="parameter-item">
                                                                        <div className="parameter-fields">
                                                                            <Input
                                                                                label="Description"
                                                                                value={parameter.description}
                                                                                onChange={(e) => this.updateParameter(operationIndex, parameterIndex, 'description', e.target.value)}
                                                                                placeholder="What is this parameter for?"
                                                                            />
                                                                            <Input
                                                                                label="Value"
                                                                                value={parameter.value}
                                                                                onChange={(e) => this.updateParameter(operationIndex, parameterIndex, 'value', e.target.value)}
                                                                                placeholder="Enter the parameter value"
                                                                            />
                                                                        </div>
                                                                        <Tooltip label="Remove parameter" position="top">
                                                                            <Icon
                                                                                icon="FiTrash2"
                                                                                size={12}
                                                                                color="gd-red"
                                                                                onIconClick={() => this.removeParameter(operationIndex, parameterIndex)}
                                                                                className="remove-parameter-icon"
                                                                            />
                                                                        </Tooltip>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="no-parameters">
                                                                <p>No parameters for this operation. Add a parameter to define the calculation.</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default ModifierEditor;
